"use client";

import { useState, useCallback, useEffect } from "react";
import posthog from "posthog-js";
import JSZip from "jszip";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { useDropzone } from "react-dropzone";
import {
  convertFile,
  getFileData,
  type DeviceOverride,
  type FileSummary,
  type StripFields,
  type TrackPoint,
} from "@/lib/client-convert";
import type { HrFixRange } from "@/lib/hr-fix";
import { RouteMap } from "@/components/route-map";
import {
  ActivityCharts,
  HrFixPanel,
  SportChanger,
  DeviceChanger,
} from "@/components/lazy";
import { SplitsTable } from "@/components/splits-table";
import { TrimControl } from "@/components/trim-control";
import { FieldStripper } from "@/components/field-stripper";
import { TimeAdjuster } from "@/components/time-adjuster";
import { EditTools } from "@/components/edit-tools";
import { type TrimRange } from "@/lib/trim";
import {
  ACCEPTED_INPUT_TYPES,
  CONVERSION_TARGETS,
  getDownloadName,
  inferTarget,
  isSupportedInput,
  type ConversionTarget,
} from "@/lib/formats";
import { buildActivityEdits, captureConversion } from "@/lib/edits";
import { triggerDownload } from "@/lib/download";
import { useEditedView } from "@/hooks/use-edited-view";
import { DownloadConfirmation } from "@/components/download-confirmation";
import { FloatingStatCard, useSimulatedRun } from "@/components/floating-stat-card";
import { AnimatedStatCard } from "@/components/animated-stat-card";
import { AffiliatePicks } from "@/components/affiliate-picks";
import {
  type UnitSystem,
  getDefaultUnitSystem,
  formatDistance,
  formatDuration,
} from "@/lib/units";

type BatchFileStatus = "pending" | "converting" | "done" | "error";

type BatchFileItem = {
  id: string;
  file: File;
  status: BatchFileStatus;
  error?: string;
  downloadUrl?: string;
  downloadName?: string;
  trim?: TrimRange;
  strip?: StripFields;
  timeShiftMs?: number;
  sportOverride?: string;
  deviceOverride?: DeviceOverride;
  hrFixRanges?: HrFixRange[];
  fileData?: {
    summary: FileSummary;
    points: TrackPoint[];
  };
};

function getTimestampedZipName(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `fitconvert-batch-${ts}.zip`;
}

export function BatchConverter() {
  const [files, setFiles] = useState<BatchFileItem[]>([]);
  const [target, setTarget] = useState<ConversionTarget>("gpx");
  const [isConverting, setIsConverting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [lastZipUrl, setLastZipUrl] = useState<string | null>(null);
  const [lastZipName, setLastZipName] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [units, setUnits] = useState<UnitSystem>("metric");
  const sim = useSimulatedRun();

  useEffect(() => {
    setUnits(getDefaultUnitSystem());
  }, []);

  useEffect(() => {
    const reset = () => {
      files.forEach((f) => {
        if (f.downloadUrl) URL.revokeObjectURL(f.downloadUrl);
      });
      setFiles([]);
      setIsDone(false);
      setSelectedFileId(null);
      if (lastZipUrl) URL.revokeObjectURL(lastZipUrl);
      setLastZipUrl(null);
      setLastZipName(null);
    };
    window.addEventListener("reset-converter", reset);
    return () => window.removeEventListener("reset-converter", reset);
  }, [files, lastZipUrl]);

  const handleFilesSelected = useCallback((newFiles: File[]) => {
    const items: BatchFileItem[] = newFiles
      .filter(isSupportedInput)
      .map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        status: "pending" as const,
      }));

    if (items.length > 0) {
      setFiles((prev) => [...prev, ...items]);
      setIsDone(false);
      if (files.length === 0 && items.length > 0) {
        setTarget(inferTarget(items[0].file));
        setSelectedFileId(items[0].id);
      }

      for (const item of items) {
        getFileData(item.file)
          .then((data) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === item.id
                  ? { ...f, fileData: data, trim: { startSec: 0, endSec: 0 } }
                  : f,
              ),
            );
          })
          .catch(() => {});
      }
    }
  }, [files.length]);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.downloadUrl) URL.revokeObjectURL(item.downloadUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    files.forEach((f) => {
      if (f.downloadUrl) URL.revokeObjectURL(f.downloadUrl);
    });
    setFiles([]);
    setIsDone(false);
    if (lastZipUrl) URL.revokeObjectURL(lastZipUrl);
    setLastZipUrl(null);
    setLastZipName(null);
  }, [files, lastZipUrl]);

  async function convertBatch() {
    setIsConverting(true);
    setIsDone(false);
    posthog.capture("batch_conversion_started", { file_count: files.length });

    const completed: { url: string; name: string }[] = [];
    let errorCount = 0;

    for (const item of files) {
      if (item.status === "done" && item.downloadUrl && item.downloadName) {
        completed.push({ url: item.downloadUrl, name: item.downloadName });
        continue;
      }

      setFiles((prev) =>
        prev.map((f) => (f.id === item.id ? { ...f, status: "converting" } : f)),
      );

      try {
        // Batch is a Pro feature, so every per-file edit applies unconditionally.
        const edits = buildActivityEdits(
          item.fileData?.points ?? [],
          {
            trim: item.trim ?? { startSec: 0, endSec: 0 },
            strip: item.strip ?? {},
            timeShiftMs: item.timeShiftMs ?? 0,
            sportOverride: item.sportOverride,
            deviceOverride: item.deviceOverride,
            hrFixRanges: item.hrFixRanges ?? [],
          },
          true
        );
        const blob = await convertFile(item.file, target, edits);
        const url = URL.createObjectURL(blob);
        const downloadName = getDownloadName(item.file, target);

        setFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? { ...f, status: "done", downloadUrl: url, downloadName }
              : f,
          ),
        );
        completed.push({ url, name: downloadName });

        captureConversion(
          posthog,
          item.fileData?.summary.format ?? "unknown",
          target,
          edits,
          { batch: true }
        );
      } catch (err) {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  status: "error",
                  error: err instanceof Error ? err.message : "Conversion failed",
                }
              : f,
          ),
        );
        errorCount++;
      }
    }

    posthog.capture("batch_conversion_completed", {
      file_count: files.length,
      success_count: completed.length,
      error_count: errorCount,
    });
    setIsConverting(false);

    if (completed.length > 0) {
      setIsDone(true);
      await downloadAllAsZip(completed);
    }
  }

  async function downloadAllAsZip(items?: { url: string; name: string }[]) {
    const toZip = items ??
      files
        .filter((f) => f.status === "done" && f.downloadUrl && f.downloadName)
        .map((f) => ({ url: f.downloadUrl!, name: f.downloadName! }));

    const zip = new JSZip();
    for (const item of toZip) {
      const response = await fetch(item.url);
      const blob = await response.blob();
      zip.file(item.name, blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const zipName = getTimestampedZipName();
    triggerDownload(url, zipName);

    if (lastZipUrl) URL.revokeObjectURL(lastZipUrl);
    setLastZipUrl(url);
    setLastZipName(zipName);
  }

  const hasFiles = files.length > 0;

  const selectedFile = files.find((f) => f.id === selectedFileId);
  const selectedSummary = selectedFile?.fileData?.summary ?? null;
  const selectedPoints = selectedFile?.fileData?.points ?? [];
  // Apply the selected file's own trim range and HR fixes to its preview (map/charts/stats/splits).
  const view = useEditedView(selectedPoints, selectedFile?.trim, selectedFile?.hrFixRanges);
  const selectedHasMap = selectedSummary?.hasGps && view.positions.length > 0;

  // Editing a file (trim or any of the editors) invalidates its already-
  // converted output and the bundled ZIP, so reset both.
  const updateSelectedItem = useCallback(
    (patch: Partial<BatchFileItem>) => {
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id !== selectedFileId) return f;
          if (f.downloadUrl) URL.revokeObjectURL(f.downloadUrl);
          return {
            ...f,
            ...patch,
            status: f.status === "done" ? "pending" : f.status,
            downloadUrl: undefined,
            downloadName: undefined,
          };
        }),
      );
      if (lastZipUrl) URL.revokeObjectURL(lastZipUrl);
      setIsDone(false);
      setLastZipUrl(null);
      setLastZipName(null);
    },
    [selectedFileId, lastZipUrl],
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) handleFilesSelected(acceptedFiles);
    },
    disabled: isConverting,
    multiple: true,
    accept: ACCEPTED_INPUT_TYPES,
    noClick: true,
    noKeyboard: true,
  });

  // Hero landing when no files loaded
  if (!hasFiles) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:py-24 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

        {/* Hero content */}
        <div className="max-w-4xl w-full text-center mb-16 relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Don&apos;t Leave <br />
            <span className="text-primary">Your Hard-Earned PRs</span> Behind.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Keep your history intact across every platform. <br className="hidden sm:inline" />
            A workout unrecorded is a workout forgotten.
          </p>
        </div>

        {/* Central converter area */}
        <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center gap-8 relative z-20">
          {/* Left floating cards */}
          <div className="hidden lg:flex flex-col gap-6 -translate-x-8">
            <FloatingStatCard
              className="-rotate-3"
              icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
              label="Distance"
              value={sim.distance}
            />
            <FloatingStatCard
              className="rotate-2 translate-x-4"
              icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
              label="Avg Pace"
              value={sim.pace}
            />
          </div>

          {/* Main upload card */}
          <div {...getRootProps()} className="w-full max-w-xl bg-slate-900/40 rounded-2xl border border-white/5 shadow-2xl p-8 backdrop-blur-sm">
            <input {...getInputProps()} aria-label="Select fitness files for batch conversion" />
            <span className="text-xs font-bold text-primary uppercase mb-4 block">Batch Mode</span>
            <div
              className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center bg-white/5 group cursor-pointer transition-colors ${
                isDragActive ? "border-primary/60 bg-primary/5" : "border-white/10 hover:border-primary/40"
              }`}
              onClick={open}
            >
              <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                <svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <h3 className="text-lg font-bold mb-1">Drop your files here</h3>
              <p className="text-sm text-muted-foreground mb-6">Convert multiple FIT, TCX, and GPX files at once</p>
              <button
                type="button"
                className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all"
                onClick={(e) => { e.stopPropagation(); open(); }}
              >
                Select Performance Files
              </button>
            </div>
          </div>

          {/* Right floating cards */}
          <div className="hidden lg:flex flex-col gap-6 translate-x-8">
            <FloatingStatCard
              className="rotate-3"
              icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
              label="Avg HR"
              value={sim.hr}
            />
            <FloatingStatCard
              className="-rotate-2 -translate-x-4"
              icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>}
              label="Avg Power"
              value={sim.power}
            />
          </div>
        </div>
      </div>
    );
  }

  // Post-download: a full-width confirmation screen (no sidebar).
  if (isDone) {
    return (
      <DownloadConfirmation
        title="Your ZIP is ready"
        fileName={lastZipName}
        onDownloadAgain={() => {
          void downloadAllAsZip();
        }}
        onReset={clearAll}
        resetLabel="Convert more files"
      />
    );
  }

  // Files loaded - batch management view
  return (
    <div className="flex-1 flex flex-col lg:flex-row p-6 gap-6 max-w-screen-2xl mx-auto w-full">
      {/* Left sidebar */}
      <aside className="w-full lg:w-80 flex flex-col gap-4">
        <div className="rounded-xl p-5 glass-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <svg className="size-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            </div>
            <div>
              <h3 className="font-bold">Batch Conversion</h3>
              <p className="text-xs text-muted-foreground">{files.length} file{files.length !== 1 ? "s" : ""} loaded</p>
            </div>
          </div>

          {/* File list */}
          <div className="space-y-1.5 max-h-[30vh] overflow-y-auto no-scrollbar mb-4">
            {files.map((item) => (
              <div
                key={item.id}
                onClick={() => item.fileData && setSelectedFileId(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  selectedFileId === item.id
                    ? "bg-primary/5 border-primary/30"
                    : "bg-background/50 border-border hover:border-muted-foreground"
                } ${item.fileData ? "cursor-pointer" : ""}`}
              >
                <StatusIcon status={item.status} />
                <span className="text-xs font-medium truncate flex-1 min-w-0">{item.file.name}</span>
                {item.status === "done" && item.downloadUrl && item.downloadName && (
                  <a
                    href={item.downloadUrl}
                    download={item.downloadName}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] text-primary hover:underline shrink-0"
                    aria-label={`Download converted file ${item.downloadName}`}
                  >
                    Save
                  </a>
                )}
                {item.status === "error" && (
                  <span className="text-[10px] text-destructive shrink-0">Failed</span>
                )}
                {!isConverting && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeFile(item.id); }}
                    className="text-[10px] text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add more files */}
          <div {...getRootProps()}>
            <input {...getInputProps()} aria-label="Add more fitness files" />
            <div
              className={`border-2 border-dashed rounded-lg p-3 flex items-center gap-3 cursor-pointer transition-colors mb-6 ${
                isDragActive ? "border-primary/60 bg-primary/5" : "border-white/10 hover:border-primary/40"
              }`}
              onClick={open}
            >
              <div className="size-7 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                <svg className="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              </div>
              <p className="text-xs font-medium text-muted-foreground">Add more files</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 block">Export as</label>
            <div className="grid grid-cols-2 gap-2">
              {CONVERSION_TARGETS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  disabled={isConverting}
                  onClick={() => setTarget(opt)}
                  className={`py-2 px-3 text-sm font-medium border rounded-lg transition-colors ${
                    target === opt
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-muted-foreground"
                  }`}
                >
                  {opt.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={convertBatch}
            disabled={isConverting}
            className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-lg hover:brightness-110 transition-all flex items-center justify-center gap-2 mb-3 shadow-lg shadow-primary/20"
          >
            {isConverting ? (
              <><Loader2 className="size-4 animate-spin" /> Converting...</>
            ) : (
              <><Download className="size-4" /> Convert &amp; Download</>
            )}
          </button>

          <button
            type="button"
            onClick={clearAll}
            disabled={isConverting}
            className="w-full py-2 text-sm text-muted-foreground font-medium hover:text-foreground transition-colors"
          >
            Clear All
          </button>
        </div>
      </aside>

      {/* Right main area */}
      <section className="flex-1 flex flex-col gap-6">
        {/* File selector tabs */}
        {files.filter((f) => f.fileData).length > 1 && (
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {files.filter((f) => f.fileData).map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setSelectedFileId(f.id)}
                className={`shrink-0 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  selectedFileId === f.id
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:border-muted-foreground"
                }`}
              >
                {f.file.name}
              </button>
            ))}
          </div>
        )}

        {/* Selected file preview — matches single-file converter */}
        {selectedFile && selectedSummary && (
          <>
            {/* File header */}
            <div className="rounded-xl p-6 glass-panel">
              <h1 className="text-3xl font-black tracking-tight mb-3">{selectedFile.file.name}</h1>
              <div className="flex flex-wrap gap-6 items-center">
                {selectedPoints.length > 0 && selectedPoints[0].timeMs > 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                    <span className="font-medium">Start Time:</span>
                    {" "}{new Date(selectedPoints[0].timeMs).toLocaleDateString(undefined, {
                      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
                    })}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                  <span className="font-medium">Trackpoints:</span> {selectedSummary.points.toLocaleString()}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  <span className="font-medium">File Type:</span> {selectedSummary.format.toUpperCase()} {selectedSummary.sport || "Activity"}
                </div>
              </div>
            </div>

            {/* Map + stats grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {selectedHasMap && (
                <div className="lg:col-span-2 relative h-[400px] rounded-xl overflow-hidden border border-white/5 glass-panel">
                  <RouteMap positions={view.positions} />
                  <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-border z-10">
                    <p className="text-xs font-bold flex items-center gap-2">
                      <svg className="size-3 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>
                      GPS PREVIEW
                    </p>
                  </div>
                </div>
              )}

              <div className={`grid grid-cols-2 ${selectedHasMap ? "lg:grid-cols-1" : "lg:col-span-3 lg:grid-cols-2 xl:grid-cols-4"} gap-4`}>
                {view.distanceM > 0 && (
                  <AnimatedStatCard label="Distance" value={formatDistance(view.distanceM, units)} />
                )}
                {view.durationMs > 0 && (
                  <AnimatedStatCard label="Duration" value={formatDuration(view.durationMs)} />
                )}
                {view.avgHr !== null && (
                  <AnimatedStatCard label="Avg Heart Rate" value={`${view.avgHr}`} unit="bpm" />
                )}
                {view.hasElevation && (
                  <AnimatedStatCard label="Elevation Gain" value={`${view.elevGainM}`} unit="m" />
                )}
              </div>
            </div>

            {/* Charts */}
            {view.viewPoints.length > 0 && (
              <ActivityCharts points={view.viewPoints} summary={selectedSummary} unitSystem={units} />
            )}

            {/* Splits */}
            {view.viewPoints.length > 0 && <SplitsTable points={view.viewPoints} unitSystem={units} />}

            {/* Per-file edits (batch is Pro-gated, so all apply) */}
            <EditTools>
              <SportChanger
                currentSport={selectedSummary.sport}
                value={selectedFile.sportOverride}
                onChange={(next) => updateSelectedItem({ sportOverride: next })}
              />

              <DeviceChanger
                currentManufacturer={selectedSummary.manufacturer}
                value={selectedFile.deviceOverride}
                onChange={(next) => updateSelectedItem({ deviceOverride: next })}
              />

              {selectedFile.trim && selectedPoints.length > 1 && (
                <TrimControl
                  points={selectedPoints}
                  value={view.range}
                  onChange={(next) => updateSelectedItem({ trim: next })}
                />
              )}

              <HrFixPanel
                points={selectedPoints}
                isPro
                hrFixRanges={selectedFile.hrFixRanges ?? []}
                onAddRange={(range) =>
                  updateSelectedItem({ hrFixRanges: [...(selectedFile.hrFixRanges ?? []), range] })
                }
                onRemoveRange={(index) =>
                  updateSelectedItem({
                    hrFixRanges: (selectedFile.hrFixRanges ?? []).filter((_, i) => i !== index),
                  })
                }
              />

              <FieldStripper
                summary={selectedSummary}
                value={selectedFile.strip ?? {}}
                onChange={(next) => updateSelectedItem({ strip: next })}
                target={target}
                onSwitchTarget={setTarget}
              />

              <TimeAdjuster
                startMs={selectedSummary.startMs}
                value={selectedFile.timeShiftMs ?? 0}
                onChange={(next) => updateSelectedItem({ timeShiftMs: next })}
              />
            </EditTools>

            {/* Affiliate picks at the bottom of the preview */}
            <AffiliatePicks count={4} />
          </>
        )}

        {/* Empty state when no file data yet */}
        {(!selectedFile || !selectedSummary) && (
          <div className="rounded-xl p-12 glass-panel flex flex-col items-center justify-center text-center">
            <p className="text-muted-foreground">Select a file to preview its data</p>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusIcon({ status }: { status: BatchFileStatus }) {
  switch (status) {
    case "pending":
      return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />;
    case "converting":
      return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
    case "done":
      return <CheckCircle2 className="h-4 w-4 text-primary" />;
    case "error":
      return <XCircle className="h-4 w-4 text-destructive" />;
  }
}
